import { Flex, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { usePersonInfoCard, type PersonInfoCardProps } from "@/entities/person-info-card/model";
import { AppCard, AppForm, AppText, FormInput, FormTextArea } from "@/shared/Components";
import { PersonAvatar } from "@/common/PersonInfoCard/Components/PersonAvatar";
import { FormButtonSelect } from "@/shared/Components/Form/FormButtonSelect";
import { InfoView } from "../info-view";
import { InfoEdit } from "../info-edit";
import styles from "./styles.module.scss";

export const PersonInfoCardDesktop = ({ personId, isEditMode }: PersonInfoCardProps) => {
  const { t } = useTranslation();
  const { state, methods } = usePersonInfoCard(personId);

  const { currentPerson, form } = state;
  const { onSubmit } = methods;

  const handleBlur = useCallback(async () => {
    const isValid = await form.trigger();

    if (isValid) {
      await onSubmit(form.getValues());
    }
  }, [form, onSubmit]);

  return (
    <Flex vertical>
      {isEditMode && (
        <AppText className={styles.hint}>
          <span>*</span> {t("contactsPage.editContactInfoHint")}
        </AppText>
      )}
      <AppCard key={currentPerson?.id} className={styles.card}>
        <div className={styles["card-content"]}>
          <div className={styles["card-inner"]}>
            {currentPerson && <PersonAvatar isEditable={isEditMode} person={currentPerson} />}
            {currentPerson && isEditMode && (
              <AppForm formApi={form} onSubmit={onSubmit}>
                <FormButtonSelect
                  placeholder={t("contactsPage.group")}
                  name="group"
                  onBlur={handleBlur}
                  options={[
                    { label: t("contactsPage.groups.other"), value: "Other" },
                    { label: t("contactsPage.groups.business"), value: "Business" },
                    { label: t("contactsPage.groups.friends"), value: "Friends" },
                    { label: t("contactsPage.groups.colleagues"), value: "Colleagues" },
                    { label: t("contactsPage.groups.family"), value: "Family" },
                  ]}
                  value={currentPerson?.group ?? "Other"}
                />
              </AppForm>
            )}
          </div>
          {currentPerson && !isEditMode && <InfoView currentPerson={currentPerson} />}
          {currentPerson && isEditMode && (
            <InfoEdit currentPerson={currentPerson} form={form} onSubmit={onSubmit} />
          )}
        </div>
      </AppCard>
      {currentPerson?.additionalInformation && !isEditMode && (
        <Space className={styles.additional}>{currentPerson?.additionalInformation}</Space>
      )}
      {currentPerson && isEditMode && (
        <AppForm formApi={form} onSubmit={onSubmit}>
          <Space direction="vertical" style={{ width: "57%", marginTop: 8 }}>
            <FormInput name="title" placeholder={t("common.title")} onBlur={handleBlur} />
            <FormTextArea
              placeholder={t("common.about")}
              name="additionalInformation"
              onBlur={handleBlur}
            />
          </Space>
        </AppForm>
      )}
    </Flex>
  );
};
